import { ApiResourceBaseClass as r } from "./baseApiCalls.js";
const e = "user";
class o extends r {
  /**
   * Retrieves information about the current authenticated user.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/me`
   *
   * @param { 'True' | 'False' } displayBusinessIds - A flag indicating whether to include business IDs in the response. Default is 'False'.
   * @returns {Promise<UserData>} A promise resolving to the user data.
   */
  getMe = async (t = "False") => {
    const s = {
      display_business_ids: t,
      platform: "web"
    }, { data: a } = await this.get(`${e}/me`, s);
    return a;
  };
  /**
   * Searches for users based on the provided query.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/search`
   *
   * @param {SearchUserParams} params - Parameters to filter the search results.
   * @returns {Promise<FuzzySearchUsersReceivedData>} - A promise resolving to the list of users matching the search query.
   */
  searchUsers = async (t) => {
    const s = `${e}/search`, a = { display_business_ids: "False", ...t }, { data: n } = await this.get(s, a);
    return n;
  };
  /**
   * Retrieves information about a specific user.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/:userId`
   *
   * @param {string} userId - The ID of the user to retrieve information about.
   * @returns {Promise<UserData>} - A promise resolving to the user data.
   */
  getUser = async (t) => {
    const { data: s } = await this.get(`${e}/${t}`);
    return s;
  };
  /**
   * Retrieves the available roles for users.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/available-roles`
   *
   * @returns {Promise<UsersAvailableRoles>} - A promise resolving to the list of available roles.
   */
  getAvailableRoles = async () => {
    const { data: t } = await this.get(
      `${e}/available-roles`
    );
    return t;
  };
  /**
   * Lists the businesses of a user.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/:userId/businesses`
   *
   * @param {string} userId - The ID of the user to retrieve their businesses from.
   * @param {number} [page] - Page number for paginated results.
   * @returns {Promise<UserBusinessesReceivedData>} - A promise resolving to the list of the businesses of the user, paginated
   */
  getUserBusiness = async (t, s) => {
    const { data: a } = await this.get(
      `${e}/${t}/businesses`,
      { page: s }
    );
    return a;
  };
  /**
   * Creates a new user.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/user`
   *
   * @param {FormattedUserData} userData - The details of the user to create.
   * @param {boolean} partoo_ui - A flag indicating whether the user is created from the front-end.
   * @returns {Promise<UserReceivedData>} - A promise resolving to the status of the request and the ID of the created user.
   */
  createUser = async (t, s = !1) => {
    const { data: a } = await this.post(
      `${e}?partoo_ui=${s}`,
      t
    );
    return a;
  };
  /**
   * Updates a user.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/user/:userId`
   *
   * @param {string} userId - The ID of the user to update.
   * @param {FormattedUserData} userData - The payload with updated user data.
   * @returns {Promise<UserReceivedData>} - A promise resolving to the status of the request and the ID of the updated user.
   */
  updateUser = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${t}`,
      s
    );
    return a;
  };
  /**
   * Creates a new user.
   *
   * HTTP Verb: `DELETE`
   * Endpoint URL: `/user/:userId`
   *
   * @param {string} userId - The ID of the user to delete.
   * @returns {Promise<UserReceivedData>} - A promise resolving to the status of the request and the ID of the deleted user.
   */
  deleteUser = async (t) => {
    const { data: s } = await this.delete(`${e}/${t}`);
    return s;
  };
  /**
   * Retrieves stats about users' status.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/stats`
   *
   * @returns {Promise<UsersStats>} - A promise resolving to the stats about users' status.
   */
  getUsersStats = async () => {
    const { data: t } = await this.get(`${e}/stats`);
    return t;
  };
  /**
   * Invite a user to create its account on the application.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/user/:userId/invite`
   *
   * @param {string} userId - The ID of the user to invite.
   * @returns {Promise<UserReceivedData>} - A promise resolving to the status of the request and the ID of the invited user.
   */
  inviteUser = async (t) => {
    const { data: s } = await this.post(`${e}/${t}/invite`);
    return s;
  };
  /**
   * Re-Invite all users with the status `invited` to create their account on the application.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/user/reinvite_all`
   *
   * @returns {Promise<SuccessResponse>} - A promise resolving to the success response of the re-invitation of the users.
   */
  reInviteAllUsers = async () => {
    const { data: t } = await this.post(`${e}/reinvite_all`);
    return t;
  };
  /**
   * Retrieves information about a specific user's report preferences.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/:userId/preferences`
   *
   * @param {string} userId - The ID of the user to retrieve the report preferences from.
   * @returns {Promise<ReportUserPreferencesResponse>} - A promise resolving to the user's report preferences.
   */
  getUserReportPreferences = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/preferences`
    );
    return s;
  };
  /**
   * Updates a user's report preferences.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/user/:userId/preferences`
   *
   * @param {string} userId - The ID of the user to update.
   * @param {UserPreferencesUpdateRequest} payload - The payload with updated user's report preferences data.
   * @returns {Promise<SuccessResponse>} - A promise that resolves to a success payload.
   */
  updateUserReportPreferences = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${s}/preferences`,
      t
    );
    return a;
  };
  /**
   * Retrieves the SSO information linked to the given email, if there is.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/sso_info`
   *
   * @param {string} email - The email of the user to retrieve the SSO information from.
   * @returns {Promise<SsoInfoResponse>} - A promise resolving to SSO information linked to the given email, if there is.
   */
  getSsoInfo = async (t) => {
    const { data: s } = await this.get(`${e}/sso_info`, {
      email: t
    });
    return s;
  };
  /**
   * Accepts the terms and conditions for the user account making the request.
   *
   * HTTP Verb: `PUT`
   * Endpoint URL: `/user/accept_terms_and_conditions`
   *
   * @returns {Promise<SuccessResponse>} - A promise that resolves to a success payload.
   */
  acceptTermsAndConditions = async () => {
    const { data: t } = await this.put(
      `${e}/accept_terms_and_conditions`
    );
    return t;
  };
  /**
   * Lists the user's accounts.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/:userId/user-accounts`
   *
   * @param {string} userId - The ID of the user to retrieve the accounts from.
   * @returns {Promise<UserAccountsResponse>} - A promise resolving to the list of the user's accounts.
   */
  getUserAccounts = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/user-accounts`
    );
    return s;
  };
  /**
   * Switches the user's account, returns a valid token for the newly connected to account.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/user/:userId/switch-user`
   *
   * @param {string} userId - The ID of the user account to connect to.
   * @returns {Promise<SwitchUserAccountResponse>} - A promise resolving to a token for the newly connected to account.
   */
  switchUserAccount = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/switch-user`
    );
    return s;
  };
  /**
   * Retrieves information about some users from their IDs.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/users/list`
   *
   * @param {string} usersList - A list of user IDs.
   * @returns {Promise<FuzzySearchUsersReceivedData>} - A promise resolving to information about the users, paginated.
   */
  listUsers = async (t) => {
    const { data: s } = await this.get("users/list", {
      user_id_in: t
    });
    return s;
  };
}
export {
  o as User
};
